import { HttpClient, HttpResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { FormControl } from "@angular/forms";
import dayjs from "dayjs";
import { MemberWorkingyear } from "projects/mijn/src/app/member/models/member";
import { defer, expand, Observable, map, reduce, EMPTY } from "rxjs";
import { FilterWithIds } from "../../_shared/components/table/sortable-table/filter-form-group";
import { OdataList } from "../../_shared/models/odataList";
import { SelectOption } from "../../_shared/models/option";
import { AddResponseMessage, UpdateResponseMessage } from "../../_shared/models/responseMessage";
import { MailService } from "../../_shared/services/mail.service";
import { QueueMail, QueueMailWithFilter } from "../../communication/pages/mail-modal/queue";
import { ExpenseItem, ExpenseTotal } from "../../expenses/models/expense";
import { StatsSettings } from "../../volunteers/models/stats-settings";
import {
  PersonAssignedFunctionBasicDto,
  PersonAssignedFunctionListDto,
} from "../models/assigned-function";
import {
  PersonDto,
  PersonForReviewDto,
  PersonImport,
  PersonListDto,
  PersonNameAndAddressDto,
  PersonWithContact,
} from "../models/person";
import { PersonListFilterFields } from "../models/person-filter-fields";
import { Salutation } from "../models/salutation";
import { LocationStatistic, VolunteerStats } from "../models/volunteer-stats";

@Injectable({
  providedIn: "root",
})
export class PersonService extends MailService<PersonDto, PersonListDto> {
  constructor(
    protected override http: HttpClient,
    @Inject("BASE_URL") protected override baseUrl: string
  ) {
    super(http, baseUrl, "person");
  }

  override sendMailsToSelection(filterFields: FilterWithIds, template: QueueMail) {
    //convert template to QueueMailWithFilter
    const templatewithfilter: QueueMailWithFilter = {
      ...template,
      Filter: filterFields,
    };
    return this.post<QueueMailWithFilter, number>("/all/mailfiltered", templatewithfilter);
  }

  getAllByParent(
    organizationId: number,
    workgroupId: number
  ): Observable<PersonNameAndAddressDto[]> {
    const pageSize = 1000;
    return defer(() => {
      let skip = 0;
      return this.getAllOdata<PersonNameAndAddressDto>(
        `/parent/all?organizationId=${organizationId}&workgroupId=${workgroupId}&$skip=0&$top=${pageSize}&$orderby=FirstName`
      ).pipe(
        expand((result) => {
          if (result.Items.length < pageSize) return EMPTY;
          skip += pageSize;
          return this.getAllOdata<PersonNameAndAddressDto>(
            `/parent/all?organizationId=${organizationId}&workgroupId=${workgroupId}&$skip=${skip}&$top=${pageSize}&$orderby=FirstName`
          );
        }),
        map((result) => result.Items),
        reduce((all, items) => all.concat(items), [] as PersonNameAndAddressDto[])
      );
    });
  }

  getAllSmall(): Observable<PersonNameAndAddressDto[]> {
    return this.getAll<PersonNameAndAddressDto>("/small");
  }

  getAllSmallSearch(
    filters: PersonListFilterFields,
    includeId: number
  ): Observable<PersonNameAndAddressDto[]> {
    return this.getAll<PersonNameAndAddressDto>(
      "/small/search?includeId=" + includeId + "&" + this.serializeForQueryStringOdata(filters)
    );
  }

  save(person: PersonDto, orgKey: string, memberKey: string): Observable<HttpResponse<string>> {
    return this.http.put<HttpResponse<string>>(
      this.baseUrl + this.controller + "/public/person/" + memberKey + "?organisationKey=" + orgKey,
      person
    );
  }

  saveMember(
    person: PersonDto,
    orgKey: string,
    memberKey: string
  ): Observable<HttpResponse<string>> {
    return this.http.put<HttpResponse<string>>(
      this.baseUrl + this.controller + "/public/" + memberKey + "?organisationKey=" + orgKey,
      person
    );
  }

  getByIdForReview(orgKey: string, accessKey: string): Observable<PersonForReviewDto> {
    return this.get("/public/" + accessKey + "/forreview?organisationKey=" + orgKey);
  }

  requestNewLink(orgKey: string, accessKey: string): Observable<HttpResponse<string>> {
    return this.http.get<HttpResponse<string>>(
      this.baseUrl +
        this.controller +
        "/public/" +
        accessKey +
        "/sendPublicAccessMail?organisationKey=" +
        orgKey
    );
  }

  getAllPersons(filters: PersonListFilterFields): Observable<OdataList<PersonListDto>> {
    return this.getAllOdata("/all?" + this.serializeForQueryStringOdata(filters));
  }

  getAllPersonsForFederation(
    filters: PersonListFilterFields
  ): Observable<OdataList<PersonListDto>> {
    return this.getAllOdata("/all/federation?" + this.serializeForQueryStringOdata(filters));
  }

  getAllPersonCount(filters: PersonListFilterFields): Observable<number> {
    filters.disablePaging();
    return this.get<number>("/all/count?" + this.serializeForQueryStringOdata(filters));
  }

  getAllMailPersonOptions(): Observable<SelectOption[]> {
    return this.getAllSmall().pipe(
      map((items) =>
        items.map(
          (p) =>
            ({
              id: p.Id,
              name: `${p.FirstName} ${p.LastName} (${this.getEmail(p)})`,
              disabled: (p.HomeEmail ?? "") == "" && (p.WorkEmail ?? "") == "",
              control: new FormControl(p),
            }) as SelectOption
        )
      )
    );
  }
  getEmail(person: PersonNameAndAddressDto): string {
    if (person.WorkEmail ?? "" != "") return person.WorkEmail;
    if (person.HomeEmail ?? "" != "") return person.HomeEmail;
    return "Geen emailadres";
  }

  //hardcoded without http
  getGenderOptions(): Observable<any> {
    return new Observable<any>((observer) => {
      observer.next([
        { id: 1, name: "M" },
        { id: 2, name: "V" },
        { id: 3, name: "X" },
      ]);
      observer.complete();
    });
  }

  // getTotalVerifiedPersons(filters: PersonListFilterFields): Observable<number> {
  //   return this.odataCount("/all?" + this.serializeForQueryStringOdata(filters));
  // }

  isVolunteer(accessKey: string, orgKey: string) {
    ///api/person/public/{accesskey}/isVolunteer?organizationKey=
    return this.get<boolean>("/public/" + accessKey + "/isVolunteer?organizationKey=" + orgKey);
  }

  getKeyByMember(accessKey: string, orgKey: string) {
    return this.get<string>("/public/keyByMember/" + accessKey + "?organizationKey=" + orgKey);
  }

  updateIBAN(personId: number, iban: string) {
    return this.put("/" + personId + "/IBAN", { IBAN: iban });
  }

  saveIBAN(iban: string, orgKey: string, accessKey: string): Observable<HttpResponse<string>> {
    return this.http.put<HttpResponse<string>>(
      this.baseUrl +
        this.controller +
        "/public/" +
        accessKey +
        "/IBAN/" +
        iban +
        "?organisationKey=" +
        orgKey,
      null
    );
  }

  setVolunteerStatus(orgKey: string, accessKey: string, statusId: number) {
    return this.http.put(
      this.baseUrl +
        this.controller +
        "/public/" +
        accessKey +
        "/volunteerstatus/" +
        statusId +
        "?organisationKey=" +
        orgKey,
      null
    );
  }

  upload(workgroupId: number | null, formData: FormData): Observable<any> {
    return this.http.post<PersonImport[]>(
      this.baseUrl + this.controller + "/upload?workgroupId=" + workgroupId,
      formData,
      { observe: "events", reportProgress: true }
    );
  }

  importPerson(person: PersonDto): Observable<number> {
    return this.http.post<number>(this.baseUrl + this.controller + "/import", person);
  }

  export(
    forImport: boolean,
    loadData: boolean = true,
    workgroupId: number | null = null
  ): Observable<Blob | null> {
    return this.http
      .get(
        this.baseUrl +
          this.controller +
          "/export?workgroupid=" +
          workgroupId +
          "&forimport=" +
          forImport +
          "&loadData=" +
          loadData,
        { responseType: "blob", observe: "response" }
      )
      .pipe(
        map((response: HttpResponse<Blob>) => {
          const blob = response.body;
          if (blob) {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Personen_${new Date()
              .toLocaleString("nl-BE", { timeZone: "Europe/Brussels" })
              .replace(/\D/g, "")
              .replace(/(\d{8})(\d{4}).*/, "$1_$2")}.xlsx`; //Personen_YYYYMMDD_HHMM.xlsx
            a.click();
            window.URL.revokeObjectURL(url);
          }
          return blob;
        })
      );
  }

  hasAnyVolunteers(): Observable<boolean> {
    return this.get<boolean>("/volunteers/any");
  }

  getBirthDays(filters: PersonListFilterFields): Observable<OdataList<PersonListDto>> {
    return this.getAllOdata("/birthdays?" + this.serializeForQueryStringOdata(filters));
  }

  public canWrite(): Observable<boolean> {
    return this.get<boolean>("/canWrite");
  }

  public canRead(id: number): Observable<boolean> {
    return this.get<boolean>("/canRead/" + id);
  }

  public override canWriteId(id: number): Observable<boolean> {
    return this.get<boolean>("/canWrite/" + id);
  }

  public getSalutations(): Observable<Salutation> {
    return this.get<Salutation>("/salutations");
  }

  public sendFederationMail(personid: number): Observable<boolean> {
    return this.post<string, boolean>("/sendfederationmail?personid=" + personid, "");
  }

  getAssignedFunctions(personId: number): Observable<PersonAssignedFunctionListDto[]> {
    return this.getAll<PersonAssignedFunctionListDto>(`/${personId}/assignedfunctions`);
  }

  addAssignedFunction(
    personId: number,
    assignedFunction: PersonAssignedFunctionBasicDto
  ): Observable<AddResponseMessage> {
    return this.post<PersonAssignedFunctionBasicDto, AddResponseMessage>(
      `/${personId}/assignedfunctions`,
      assignedFunction
    );
  }

  updateAssignedFunction(
    personId: number,
    assignedFunction: PersonAssignedFunctionBasicDto
  ): Observable<UpdateResponseMessage> {
    return this.put<PersonAssignedFunctionBasicDto, UpdateResponseMessage>(
      `/${personId}/assignedfunctions`,
      assignedFunction
    );
  }

  deleteAssignedFunction(personId: number, assignedFunctionId: number) {
    return this.delete(`/${personId}/assignedfunctions/${assignedFunctionId}`);
  }

  stopAllAssignedFunctions(personId: number, assignedFunctionsIds: number[]) {
    let filterString: string[] = [];
    assignedFunctionsIds.forEach((id) => {
      filterString.push(`assignedfunctionids=${id}`);
    });
    return this.post(
      `/${personId}/assignedfunctions/stop${filterString.length > 0 ? "?" : ""}${filterString.join("&")}`,
      null
    );
  }

  getStatistics(settings: StatsSettings): Observable<VolunteerStats> {
    return this.post<StatsSettings, VolunteerStats>("/VolunteerStatistics", settings);
  }

  getLocationSatistics(settings: StatsSettings): Observable<LocationStatistic[]> {
    return this.post<StatsSettings, LocationStatistic[]>("/VolunteerStatistics/location", settings);
  }

  public postToOmira(personid: number, wyId: string | null): Observable<any> {
    return this.post<any>("/" + personid + "/omira/" + wyId, "");
  }

  public getWorkYearsWithMemberIds(personid: number) {
    return this.getAll<MemberWorkingyear>(`/${personid}/workingyears`);
  }

  getFederationNumberFromFederationPerson(personId: number) {
    return this.get<string>(`/${personId}/federationperson/number`);
  }

  getExpenseTotal(personId: number, year: number): Observable<ExpenseTotal> {
    return this.get<ExpenseTotal>(`/${personId}/flatRateExpenses/total?year=${year}`);
  }

  getDayExpenseTotal(personId: number, date: string): Observable<number> {
    return this.get<number>(
      `/${personId}/flatRateExpenses/total?date=${dayjs(date).format("YYYY-MM-DD")}`
    );
  }

  getExpensesByDate(personId: number, date: string): Observable<ExpenseItem[]> {
    return this.getAll<ExpenseItem>(
      `/${personId}/flatRateExpenses?date=${dayjs(date).format("YYYY-MM-DD")}`
    );
  }

  countAllWithMissingFields(): Observable<number> {
    return this.get<number>("/CountAllWithMissingFields");
  }

  getAllWithMissingFields(
    filters: PersonListFilterFields
  ): Observable<OdataList<PersonWithContact>> {
    return this.getAllOdata("/withMissingFields?" + this.serializeForQueryStringOdata(filters));
  }

  getAllUpdated(): Observable<PersonListDto[]> {
    return this.getAll("/updated");
  }
}
